import { axiosInstance } from './apiCommon';

import { PagedResult } from 'types/search-common/PagedResult';
import { FortnoxCompanyInvoice } from 'types/fortnox/FortnoxCompanyInvoice';
import { BaseQueryParams } from 'types/types';

export interface FortnoxCompanyInvoicesResult extends PagedResult<FortnoxCompanyInvoice> {
  lastSyncedAt?: string | null;
}

const extractFileNameFromContentDisposition = (contentDisposition?: string): string | null => {
  if (!contentDisposition) {
    return null;
  }

  const utf8FileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8FileNameMatch?.[1]) {
    return decodeURIComponent(utf8FileNameMatch[1].replace(/['\"]/g, ''));
  }

  const fileNameMatch = contentDisposition.match(/filename=\"?([^;\"]+)\"?/i);
  if (fileNameMatch?.[1]) {
    return fileNameMatch[1].trim();
  }

  return null;
};

const sanitizeFileNamePart = (value: string): string => {
  const sanitized = value.trim().replace(/[^a-zA-Z0-9_-]/g, '_');
  return sanitized || 'value';
};

const normalizeInvoiceDateForFileName = (invoiceDate?: string | null): string => {
  if (!invoiceDate) {
    return 'date';
  }

  const isoDateMatch = invoiceDate.match(/^\d{4}-\d{2}-\d{2}/);
  return isoDateMatch?.[0] ?? sanitizeFileNamePart(invoiceDate);
};

const buildFallbackInvoicePreviewFileName = (
  invoiceNumber: string,
  invoiceDate?: string | null,
  customerName?: string | null
): string => {
  const safeCustomerName = sanitizeFileNamePart(customerName || 'company');
  const safeDocumentNumber = sanitizeFileNamePart(invoiceNumber || 'invoice');
  const safeDate = normalizeInvoiceDateForFileName(invoiceDate);
  return `${safeCustomerName}_${safeDocumentNumber}_${safeDate}.pdf`;
};

export const fetchCompanyInvoices = async (
  companyId: string,
  pageNumber: number,
  pageSize: number
): Promise<FortnoxCompanyInvoicesResult> => {
  const response = await axiosInstance.get<FortnoxCompanyInvoicesResult>(`/api/v1/companies/${companyId}/fortnox/invoices`, {
    params: {
      pageNumber,
      pageSize,
    },
  });
  return response.data;
};

export const syncCompanyInvoices = async (companyId: string): Promise<void> => {
  await axiosInstance.post(`/api/v1/Fortnox/company/${companyId}/invoices/sync`);
};

export const updateCompaniesBillingStatus = async (): Promise<void> => {
  await axiosInstance.post('/api/v1/Fortnox/billing-status/update');
};

export const downloadFortnoxInvoicePreview = async (
  companyId: string,
  invoiceNumber: string,
  invoiceDate?: string | null,
  customerName?: string | null,
  params?: BaseQueryParams
): Promise<void> => {
  const response = await axiosInstance.get(
    `/api/v1/Fortnox/company/${companyId}/invoice/${encodeURIComponent(invoiceNumber)}/preview`,
    {
      responseType: 'blob',
      ...params,
    }
  );

  const fileNameFromHeader = extractFileNameFromContentDisposition(response.headers?.['content-disposition']);
  const fileName = fileNameFromHeader || buildFallbackInvoicePreviewFileName(invoiceNumber, invoiceDate, customerName);
  const pdfBlob = new Blob([response.data], { type: 'application/pdf' });

  const navigatorWithMsSave = window.navigator as Navigator & {
    msSaveOrOpenBlob?: (blob: Blob, defaultName?: string) => boolean;
  };

  if (navigatorWithMsSave.msSaveOrOpenBlob) {
    navigatorWithMsSave.msSaveOrOpenBlob(pdfBlob, fileName);
    return;
  }

  const pdfUrl = URL.createObjectURL(pdfBlob);
  const downloadLink = document.createElement('a');
  downloadLink.href = pdfUrl;
  downloadLink.download = fileName;
  downloadLink.style.display = 'none';
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);

  window.setTimeout(() => {
    URL.revokeObjectURL(pdfUrl);
  }, 30_000);
};
